import { useState, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, ExternalLink, Navigation, Globe, Building2, Eye, EyeOff } from 'lucide-react';
import Section from '../components/Section';
import Button from '../components/Button';
import PageHero from '../components/PageHero';
import { SF_UI } from '../components/ui/tokens';
import OpenLayersMapEnhanced, { CONTINENT_CENTERS } from '../components/OpenLayersMapEnhanced';
import LocationSidebar from '../components/LocationSidebar';
import LocationSearchBar from '../components/LocationSearchBar';
import ContinentPills from '../components/ContinentPills';
import LiveStatsBar from '../components/LiveStatsBar';
import MapInfoCard from '../components/MapInfoCard';
import { 
  allLocations, 
  activeOffices, 
  searchLocations, 
  getLocationsByContinent,
  getActiveOfficesByLocationId
} from '../data/LocationData';

const SportsOfficesPageEnhanced = () => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [hoveredLocationId, setHoveredLocationId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [activeContinent, setActiveContinent] = useState(null);
  const [showPlannedOnMap, setShowPlannedOnMap] = useState(false); // New toggle state
  const mapControlsRef = useRef(null);

  // Calculate stats
  const stats = useMemo(() => {
    const totalLocations = allLocations.length;
    const activeLocations = allLocations.filter(loc => loc.status === 'active').length;
    const plannedLocations = allLocations.filter(loc => loc.status === 'planned').length;
    const countries = allLocations.filter(loc => loc.type === 'country').length;
    const usStates = allLocations.filter(loc => loc.type === 'state' && loc.country === 'United States').length;
    const caProvinces = allLocations.filter(loc => loc.type === 'province' && loc.country === 'Canada').length;
    const continents = [...new Set(allLocations.map(loc => loc.continent))].length;

    return {
      totalLocations,
      activeLocations,
      plannedLocations,
      countries,
      usStates,
      caProvinces,
      continents,
      activeOffices: activeOffices.length
    };
  }, []);

  // Filter locations based on all criteria
  const filteredLocations = useMemo(() => {
    let locations = [...allLocations];

    // Apply search filter
    if (searchTerm) {
      locations = searchLocations(searchTerm);
    }

    // Apply continent filter from pills
    if (activeContinent) {
      locations = locations.filter(loc => loc.continent === activeContinent);
    }

    // Apply category filter
    switch (filter) {
      case 'active':
        locations = locations.filter(loc => loc.status === 'active');
        break;
      case 'planned':
        locations = locations.filter(loc => loc.status === 'planned');
        break;
      case 'countries':
        locations = locations.filter(loc => loc.type === 'country');
        break;
      case 'states':
        locations = locations.filter(loc => loc.type === 'state');
        break;
      case 'provinces':
        locations = locations.filter(loc => loc.type === 'province');
        break;
      default:
        break;
    }

    return locations;
  }, [searchTerm, filter, activeContinent]);

  // Get active offices for selected location
  const selectedLocationOffices = useMemo(() => {
    if (!selectedLocation) return [];
    return activeOffices.filter(
      office => office.locationId === parseInt(selectedLocation.id)
    );
  }, [selectedLocation]);

  // Handle map ready
  const handleMapReady = useCallback((controls) => {
    mapControlsRef.current = controls;
  }, []);

  // Handle continent selection
  const handleContinentSelect = useCallback((continent) => {
    setActiveContinent(continent);
    if (continent && mapControlsRef.current?.flyToContinent) {
      mapControlsRef.current.flyToContinent(continent);
    } else if (!continent && mapControlsRef.current?.resetView) {
      mapControlsRef.current.resetView();
    }
  }, []);

  // Handle location select
  const handleLocationSelect = useCallback((location) => {
    setSelectedLocation(location);
  }, []);

  // Handle location hover
  const handleLocationHover = useCallback((locationId) => {
    setHoveredLocationId(locationId);
  }, []);

  // Close info card
  const handleCloseInfoCard = useCallback(() => {
    setSelectedLocation(null);
  }, []);

  // Handle "Find nearest to me"
  const handleFindNearest = useCallback(async () => {
    // Check if we have locations to search
    if (filteredLocations.length === 0) {
      alert('No locations available to search');
      return;
    }

    // Function to find nearest location given coordinates
    const findNearestLocation = (latitude, longitude) => {
      let nearestLocation = null;
      let minDistance = Infinity;

      filteredLocations.forEach(loc => {
        if (!loc.coordinates || loc.coordinates.length < 2) return;
        
        const [locLon, locLat] = loc.coordinates;
        const dLat = locLat - latitude;
        const dLon = locLon - longitude;
        const distance = Math.sqrt(dLat * dLat + dLon * dLon);
        
        if (distance < minDistance) {
          minDistance = distance;
          nearestLocation = loc;
        }
      });

      return nearestLocation;
    };

    // Function to handle found location
    const handleLocationFound = (latitude, longitude, source) => {
      console.log(`Location found via ${source}:`, latitude, longitude);
      
      const nearestLocation = findNearestLocation(latitude, longitude);
      
      if (nearestLocation) {
        console.log('Nearest location:', nearestLocation.name);
        setSelectedLocation(nearestLocation);
        
        if (mapControlsRef.current?.flyTo) {
          mapControlsRef.current.flyTo(nearestLocation, 5);
        }
      } else {
        alert('Could not find a nearby location');
      }
    };

    // Try browser geolocation first
    const tryBrowserGeolocation = () => {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              source: 'browser'
            });
          },
          (error) => {
            reject(error);
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 300000
          }
        );
      });
    };

    // Fallback: IP-based geolocation
    const tryIPGeolocation = async () => {
      try {
        // Try ipapi.co (free, no API key needed)
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) throw new Error('IP geolocation failed');
        
        const data = await response.json();
        if (data.latitude && data.longitude) {
          return {
            latitude: data.latitude,
            longitude: data.longitude,
            source: 'IP address'
          };
        }
        throw new Error('No coordinates in response');
      } catch (err) {
        // Try backup service
        const response = await fetch('https://ip-api.com/json/');
        if (!response.ok) throw new Error('Backup IP geolocation failed');
        
        const data = await response.json();
        if (data.lat && data.lon) {
          return {
            latitude: data.lat,
            longitude: data.lon,
            source: 'IP address'
          };
        }
        throw new Error('Could not determine location');
      }
    };

    // Try browser first, then fall back to IP
    try {
      console.log('Trying browser geolocation...');
      const result = await tryBrowserGeolocation();
      handleLocationFound(result.latitude, result.longitude, result.source);
    } catch (browserError) {
      console.log('Browser geolocation failed, trying IP-based...', browserError.message);
      
      try {
        const result = await tryIPGeolocation();
        handleLocationFound(result.latitude, result.longitude, result.source);
      } catch (ipError) {
        console.error('All geolocation methods failed:', ipError);
        alert('Unable to determine your location. Please try again or select a location manually.');
      }
    }
  }, [filteredLocations]);

  // Determine if we should force show planned locations
  // Show if toggle is ON or if filter is specifically set to 'planned'
  const showPlannedLocations = showPlannedOnMap || filter === 'planned';

  return (
    <>
    <PageHero
      eyebrow="Community · Sports Offices"
      title="Sports Offices"
      subtitle="Explore active and planned SportsFuture offices worldwide — find your region and connect with officers on the map."
      icon={<Globe size={28} className="text-primary-100" />}
      compact
    />
    <Section className={`pt-10 pb-16 min-h-screen ${SF_UI.systemId}`} bgColor={SF_UI.sectionBand}>
      <div className="mx-auto max-w-[1400px]">

        {/* Funding Status Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mb-12 bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center"
        >
          <div className="max-w-2xl mx-auto flex flex-col items-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-3">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Work in Progress
            </div>
            <h3 className="text-lg font-semibold text-amber-900 mb-2">
              Athlete Funding is Coming Soon
            </h3>
            <p className="text-amber-800 text-sm leading-relaxed">
              Our platform is still in its early stages, and we're laying the groundwork to support athletes financially. 
              Thank you for your patience as we build something meaningful.
            </p>
          </div>
        </motion.div>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Sports Offices Worldwide
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore our global network of sports offices across 6 continents
          </p>
        </motion.div>

        {/* Search & Continent Pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-4 space-y-4"
        >
          <LocationSearchBar
            searchTerm={searchTerm}
            filter={filter}
            onSearchChange={setSearchTerm}
            onFilterChange={setFilter}
            totalLocations={allLocations.length}
            filteredCount={filteredLocations.length}
          />
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <ContinentPills
              locations={allLocations}
              activeContinent={activeContinent}
              onContinentSelect={handleContinentSelect}
            />
            
            <div className="flex items-center gap-2 self-start sm:self-auto">
              {/* Show Planned Toggle Button */}
              <button
                onClick={() => setShowPlannedOnMap(!showPlannedOnMap)}
                className={`
                  inline-flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-medium
                  transition-all duration-200
                  ${showPlannedOnMap 
                    ? 'bg-gray-700 border-gray-700 text-white hover:bg-gray-800' 
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                  }
                `}
                title={showPlannedOnMap ? 'Hide planned locations' : 'Show planned locations'}
              >
                {showPlannedOnMap ? (
                  <Eye className="w-4 h-4" />
                ) : (
                  <EyeOff className="w-4 h-4" />
                )}
                <span>Planned</span>
                <span className={`
                  px-1.5 py-0.5 rounded-md text-xs font-semibold
                  ${showPlannedOnMap 
                    ? 'bg-gray-600 text-gray-200' 
                    : 'bg-gray-100 text-gray-500'
                  }
                `}>
                  {stats.plannedLocations}
                </span>
              </button>

              {/* Near Me Button */}
              <button
                onClick={handleFindNearest}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 
                         rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 
                         hover:border-gray-300 transition-all duration-200"
              >
                <Navigation className="w-4 h-4" />
                <span>Near me</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Live Stats Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-6"
        >
          <LiveStatsBar
            allLocations={allLocations}
            filteredLocations={filteredLocations}
          />
        </motion.div>

        {/* Main Dashboard Layout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6"
        >
          {/* Sidebar */}
          <div className="order-2 lg:order-1">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Sidebar Header */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800 text-sm">Locations</h3>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {filteredLocations.length} results
                  </span>
                </div>
              </div>
              
              {/* Location List */}
              <div className="p-2">
                <LocationSidebar
                  locations={filteredLocations}
                  selectedLocation={selectedLocation}
                  hoveredLocationId={hoveredLocationId}
                  onLocationSelect={handleLocationSelect}
                  onLocationHover={handleLocationHover}
                  maxHeight="calc(100vh - 420px)"
                />
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="order-1 lg:order-2 relative">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <OpenLayersMapEnhanced
                locations={filteredLocations}
                selectedLocation={selectedLocation}
                hoveredLocationId={hoveredLocationId}
                onLocationSelect={handleLocationSelect}
                onLocationHover={handleLocationHover}
                onMapReady={handleMapReady}
                showPlannedLocations={showPlannedLocations}
                className="h-[400px] sm:h-[500px] lg:h-[calc(100vh-320px)] min-h-[400px]"
              />
            </div>

            {/* Info Card Overlay */}
            {selectedLocation && (
              <div className="absolute inset-0 pointer-events-none z-30">
                <div className="pointer-events-auto">
                  <MapInfoCard
                    location={selectedLocation}
                    activeOffices={selectedLocationOffices}
                    onClose={handleCloseInfoCard}
                    onViewDetails={() => {
                      console.log('View details:', selectedLocation);
                    }}
                    className="!absolute !top-4 !left-2 !right-2 sm:!left-auto sm:!right-16 !transform-none"
                  />
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-12 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white p-5 rounded-2xl shadow-md sf-surface-card sf-ui-system text-center border border-gray-200"
          >
            <div className="text-3xl font-bold text-blue-600 mb-1">{stats.totalLocations}</div>
            <div className="text-sm text-gray-600">Total Locations</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="bg-white p-5 rounded-2xl shadow-md sf-surface-card sf-ui-system text-center border border-gray-200"
          >
            <div className="text-3xl font-bold text-green-600 mb-1">{stats.activeOffices}</div>
            <div className="text-sm text-gray-600">Active Offices</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white p-5 rounded-2xl shadow-md sf-surface-card sf-ui-system text-center border border-gray-200"
          >
            <div className="text-3xl font-bold text-blue-600 mb-1">{stats.countries}</div>
            <div className="text-sm text-gray-600">Countries</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="bg-white p-5 rounded-2xl shadow-md sf-surface-card sf-ui-system text-center border border-gray-200"
          >
            <div className="text-3xl font-bold text-blue-600 mb-1">{stats.usStates}</div>
            <div className="text-sm text-gray-600">US States</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white p-5 rounded-2xl shadow-md sf-surface-card sf-ui-system text-center border border-gray-200"
          >
            <div className="text-3xl font-bold text-blue-600 mb-1">{stats.caProvinces}</div>
            <div className="text-sm text-gray-600">CA Provinces</div>
          </motion.div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system text-center border border-gray-200"
          >
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center text-blue-600">
                <Globe className="w-7 h-7" />
              </div>
            </div>
            <h3 className="text-lg font-semibold mb-2 text-gray-900">Global Presence</h3>
            <p className="text-gray-600 text-sm">
              Comprehensive coverage across all continents with offices in key regions worldwide.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system text-center border border-gray-200"
          >
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center text-green-600">
                <Building2 className="w-7 h-7" />
              </div>
            </div>
            <h3 className="text-lg font-semibold mb-2 text-gray-900">Local Support</h3>
            <p className="text-gray-600 text-sm">
              Dedicated sports officers providing personalized assistance in your region.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system text-center border border-gray-200"
          >
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center text-blue-600">
                <Users className="w-7 h-7" />
              </div>
            </div>
            <h3 className="text-lg font-semibold mb-2 text-gray-900">Community Network</h3>
            <p className="text-gray-600 text-sm">
              Connect with local athletes and sports communities through our office network.
            </p>
          </motion.div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-8 text-center border border-blue-100"
        >
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              <Users className="w-4 h-4" />
              Join our network
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Want to Open a Sports Office?
            </h2>
            <p className="text-gray-600 mb-6">
              Become a Sports Officer in your region. Help connect athletes with opportunities 
              and build the future of sports funding.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Button to="/apply" variant="primary" size="lg">
                Apply to be a Sports Officer
              </Button>
              <Button to="/contact" variant="secondary" size="lg">
                Learn More
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
    </>
  );
};

export default SportsOfficesPageEnhanced;
